import React, { useState, useEffect } from 'react';
import { Bot } from 'lucide-react';
import { getOptimizedImage } from "@/lib/utils";

export const FloatingAiAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    // Dynamically load the chat widget script to improve page speed
    const loadChatWidget = () => {
      if (document.getElementById('leadconnector-chat-widget')) return;
      
      const script = document.createElement('script');
      script.id = 'leadconnector-chat-widget';
      script.src = "https://widgets.leadconnectorhq.com/loader.js";
      script.dataset.resourcesUrl = "https://widgets.leadconnectorhq.com/chat-widget/loader.js";
      script.dataset.widgetId = "69f291586dc5ee560c3b6457";
      script.async = true;
      document.body.appendChild(script);
    };

    // Load after a short delay to not block the main thread
    const timer = setTimeout(loadChatWidget, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleWidget = () => {
      if (isTransitioning) return; // Skip hiding logic while opening

      const widget = document.querySelector('chat-widget') as HTMLElement;
      if (!widget || !widget.shadowRoot) return;

      let isChatWindowVisible = false;
      const elements = Array.from(widget.shadowRoot.querySelectorAll('div, iframe'));
      
      for (const el of elements) {
        const rect = el.getBoundingClientRect();
        // Chat window is usually large
        if (rect.width > 150 && rect.height > 150) {
          let node: Element | null = el;
          let visible = true;
          while (node && node.nodeType === 1) {
            const style = window.getComputedStyle(node);
            if (
              style.display === 'none' || 
              style.opacity === '0' || 
              style.visibility === 'hidden' || 
              style.pointerEvents === 'none' ||
              style.transform.includes('scale(0)')
            ) {
              visible = false;
              break;
            }
            node = node.parentNode instanceof ShadowRoot ? null : node.parentNode as Element;
          }
          if (visible) {
            isChatWindowVisible = true;
            break;
          }
        }
      }

      let isBubbleVisible = false;
      let isCloseBtn = false;
      const buttons = Array.from(widget.shadowRoot.querySelectorAll('button'));
      const bubbleBtn = buttons.find(b => {
        const rect = b.getBoundingClientRect();
        return rect.width > 30 && rect.width < 100 && rect.height > 30 && rect.height < 100;
      });

      if (bubbleBtn) {
        let node: Element | null = bubbleBtn;
        let visible = true;
        while (node && node.nodeType === 1) {
          const style = window.getComputedStyle(node);
          if (
            style.display === 'none' || 
            style.opacity === '0' || 
            style.visibility === 'hidden' || 
            style.pointerEvents === 'none' || 
            style.transform.includes('scale(0)')
          ) {
            visible = false;
            break;
          }
          node = node.parentNode instanceof ShadowRoot ? null : node.parentNode as Element;
        }
        isBubbleVisible = visible;
        
        if (visible) {
          const label = (bubbleBtn.getAttribute('aria-label') || '').toLowerCase();
          isCloseBtn = label.includes('close') || label.includes('schließ') || label.includes('schliess') || label.includes('minimize') || label.includes('back');
        }
      }

      let nextIsOpen = isOpen; // default to current state
      
      if (isChatWindowVisible) {
        nextIsOpen = true;
      } else if (isBubbleVisible) {
        if (isCloseBtn) {
          nextIsOpen = true;
        } else {
          nextIsOpen = false;
        }
      }

      if (nextIsOpen) {
        setIsOpen(true);
        widget.style.setProperty('opacity', '1', 'important');
        widget.style.setProperty('pointer-events', 'auto', 'important');
        widget.style.setProperty('z-index', '2147483647', 'important');
      } else {
        setIsOpen(false);
        widget.style.setProperty('opacity', '0', 'important');
        widget.style.setProperty('pointer-events', 'none', 'important');
        widget.style.setProperty('z-index', '-1', 'important');
      }
    };
    
    const interval = setInterval(handleWidget, 150);
    return () => clearInterval(interval);
  }, [isOpen, isTransitioning]);

  const toggleChat = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const widget = document.querySelector('chat-widget') as HTMLElement;
    if (!widget || !widget.shadowRoot) return;

    const buttons = Array.from(widget.shadowRoot.querySelectorAll('button'));
    const openBtn = buttons.find(b => {
      const label = (b.getAttribute('aria-label') || '').toLowerCase();
      return label.includes('open') || label.includes('öffnen') || label.includes('oeffnen') || label.includes('chat');
    }) || buttons[0];

    if (!isOpen && openBtn) {
      setIsTransitioning(true);
      setIsOpen(true);
      
      // Make widget visible and interactive first so programmatic click registers on mobile
      widget.style.setProperty('opacity', '1', 'important');
      widget.style.setProperty('pointer-events', 'auto', 'important');
      widget.style.setProperty('z-index', '2147483647', 'important');
      
      // Short delay to allow DOM to update before clicking
      setTimeout(() => {
        openBtn.click();
        
        // Reset transitioning state after animation completes
        setTimeout(() => {
          setIsTransitioning(false);
        }, 3000);
      }, 50);
    }
  };

  return (
    <div 
      className={`fixed bottom-6 right-6 transition-all duration-300 flex flex-col gap-4 items-center ${isOpen ? 'opacity-0 pointer-events-none scale-75' : 'opacity-100 scale-100'}`} 
      style={{ zIndex: 2147483646 }}
    >
      {/* WhatsApp Button */}
      <a 
        href="https://wa.me/4915208474722"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Kontakt per WhatsApp"
        className="floating-wa-button relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 transform touch-manipulation"
        style={{
          background: 'linear-gradient(135deg, rgba(37,211,102,0.8) 0%, rgba(18,140,126,0.8) 100%)',
          boxShadow: '0 0 20px rgba(37, 211, 102, 0.7), 0 0 40px rgba(37, 211, 102, 0.5), 0 0 60px rgba(37, 211, 102, 0.3)',
          border: '2px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        {/* 3D effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-b from-white/20 to-transparent opacity-30"></div>
        
        {/* Inner glow */}
        <div className="absolute inset-0 rounded-full border-2 border-white/10"></div>
        
        {/* WhatsApp Icon */}
        <div className="relative z-10">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
          </svg>
        </div>
        
        {/* Glowing animation */}
        <div className="absolute inset-0 rounded-full animate-ping opacity-20 bg-[#25D366]"></div>
      </a>

      {/* Floating 3D Glowing AI Logo */}
      <button 
        className="floating-ai-button relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 transform touch-manipulation"
        onClick={toggleChat}
        aria-label="KI Assistent öffnen"
        style={{
          background: 'linear-gradient(135deg, rgba(40,170,225,0.8) 0%, rgba(30,140,190,0.8) 100%)',
          boxShadow: '0 0 20px rgba(40, 170, 225, 0.7), 0 0 40px rgba(40, 170, 225, 0.5), 0 0 60px rgba(40, 170, 225, 0.3)',
          border: '2px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        {/* 3D effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-b from-white/20 to-transparent opacity-30"></div>
        
        {/* Inner glow */}
        <div className="absolute inset-0 rounded-full border-2 border-white/10"></div>
        
        {/* AI Icon */}
        <div className="relative z-10">
          <Bot className="w-8 h-8 text-white" />
        </div>
        
        {/* Glowing animation */}
        <div className="absolute inset-0 rounded-full animate-ping opacity-20 bg-[#28aae1]"></div>
      </button>
      
      {/* CSS for animations */}
      <style>{`
        .floating-ai-button:hover {
          transform: scale(1.1) rotate(5deg);
          box-shadow: 0 0 30px rgba(40, 170, 225, 0.9), 0 0 50px rgba(40, 170, 225, 0.7), 0 0 70px rgba(40, 170, 225, 0.5) !important;
        }
        .floating-wa-button:hover {
          transform: scale(1.1) rotate(5deg);
          box-shadow: 0 0 30px rgba(37, 211, 102, 0.9), 0 0 50px rgba(37, 211, 102, 0.7), 0 0 70px rgba(37, 211, 102, 0.5) !important;
        }
      `}</style>
    </div>
  );
};
